import { type } from 'arktype';
import { isoDateString } from './labour-pool-claim.model';
import { rruleSchema } from '@zag/common/validity';

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export const labourPoolRequestStatuses = ['open', 'partially_fulfilled', 'fulfilled', 'cancelled', 'expired'] as const;
export type LabourPoolRequestStatus = (typeof labourPoolRequestStatuses)[number];

export const labourPoolRequestPriorities = ['low', 'medium', 'high'] as const;
export type LabourPoolRequestPriority = (typeof labourPoolRequestPriorities)[number];

// ---------------------------------------------------------------------------
// Domain type — returned by the API
// ---------------------------------------------------------------------------

export interface LabourPoolRequest {
  id: string;
  requestingTeamId: string;
  requestingTeamName: string;
  requestedBy: string;
  requestedByName: string;
  planInstanceId: string | null;
  startDate: string;
  endDate: string;
  rrule: string | null;
  requiredSkills: string[];
  headcount: number;
  /**
   * Story 23-13: total person-days budget required by this request. The canonical
   * fulfilment metric. Independent of `headcount` (which caps concurrent assignees).
   */
  personDays: number;
  /**
   * Story 23-13: when true, every day in the window must be staffed by `headcount`
   * concurrent assignees (legacy semantic). Default false (new person-days semantic).
   */
  concurrent: boolean;
  /** When false, the request must be fulfilled by a single claim covering the full window. */
  isSplittable: boolean;
  priority: LabourPoolRequestPriority;
  notes: string | null;
  status: LabourPoolRequestStatus;
  /** Number of approved claims linked to this request */
  fulfilledHeadcount: number;
  /** Remaining demand: max(0, headcount - fulfilledHeadcount) */
  remainingHeadcount: number;
  /**
   * Story 23-13: sum of approved-claim windows clipped to the request window.
   * Use with `personDays` to render budget-style fulfilment in person-days mode.
   */
  assignedPersonDays: number;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Create schema — POST /api/v1/labour-pool/requests
// ---------------------------------------------------------------------------

export const createLabourPoolRequestSchema = type({
  requestingTeamId: type('string').configure({
    description: 'ID of the team that needs additional headcount.',
    examples: ['team_abc123'],
  }),
  'planInstanceId?': type('string | null').configure({
    description: 'Optional plan instance the request is scoped to. `null` clears the linkage.',
    examples: ['planinst_abc123'],
  }),
  startDate: isoDateString.configure({
    description: 'Inclusive start of the need window as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  endDate: isoDateString.configure({
    description: 'Inclusive end of the need window as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
  'rrule?': rruleSchema.configure({
    description: 'Optional iCal RRULE body refining which dates the request is active.',
    examples: ['FREQ=WEEKLY;BYDAY=MO,WE'],
  }),
  'requiredSkills?': type('string[]').configure({
    description: 'IDs of skills required by the requested floaters. Defaults to an empty array.',
    examples: [['skill_abc123']],
  }),
  'headcount?': type('number').configure({
    description:
      'Max concurrent assignees per day. When `concurrent=true`, this is also the per-day fulfilment target. When `concurrent=false` (default), it is purely a cap. Defaults to 1.',
    examples: [2],
  }),
  'personDays?': type('number > 0').configure({
    description:
      'Story 23-13: total person-days budget. When omitted, the API auto-fills `headcount × windowDays` to preserve the legacy concurrent-demand semantic.',
    examples: [7],
  }),
  'concurrent?': type('boolean').configure({
    description:
      'Story 23-13: when true, fulfilment requires every day to have `headcount` concurrent assignees (legacy). When false (default), fulfilment is measured by person-days budget only.',
    examples: [false],
  }),
  'isSplittable?': type('boolean').configure({
    description:
      'When false, the request must be fulfilled by a single claim covering the full window. Defaults to true (temporal splitting allowed).',
    examples: [true],
  }),
  priority: type.enumerated(...labourPoolRequestPriorities).configure({
    description: "Urgency of the request. 'low' = nice to have, 'medium' = normal, 'high' = critical.",
    examples: ['medium'],
  }),
  'notes?': type('string | null').configure({
    description: 'Optional free-form rationale shared with offering teams. `null` clears the note.',
    examples: ['Need strong reception coverage over the school holidays.'],
  }),
}).configure({ description: 'Payload for creating a new Labour Pool request (need for additional headcount).' });

export type CreateLabourPoolRequest = typeof createLabourPoolRequestSchema.infer;

// ---------------------------------------------------------------------------
// Query schema — GET /api/v1/labour-pool/requests
// ---------------------------------------------------------------------------

export const labourPoolRequestQuerySchema = type({
  'requestingTeamId?': 'string',
  'status?': type.enumerated(...labourPoolRequestStatuses),
  'fromDate?': 'string',
  'toDate?': 'string',
  /** Return only requests whose window intersects this offer and whose required skills are satisfied by the offer's employee */
  'matchingOfferId?': 'string',
});

export type LabourPoolRequestQuery = typeof labourPoolRequestQuerySchema.infer;
